import {
  CONNECTION_ERROR_TITLE,
  CONNECTION_ERROR_MESSAGE,
  CONNECTION_ERROR_RETRY_LABEL,
  CONNECTION_ERROR_LOGO_URL,
} from '../constants/bookingConst';
import '../css/ConnectionErrorScreen.css';

/**
 * The club's logo, shown where the artwork goes.
 *
 * Sourced from public/logo.png (CONNECTION_ERROR_LOGO_URL) rather than the
 * logo configured in Config -> Branding: this screen exists precisely because
 * the config could not be read, so the configured URL is the one thing that
 * cannot be relied on here.
 *
 * `alt=""` because the headline and message already carry the meaning — a
 * client that never replaced the placeholder logo should not have it read out
 * as content. On a missing or unreadable file the element hides itself rather
 * than leaving a broken-image icon in the middle of an apology screen.
 *
 * @returns {JSX.Element}
 */
const ClientLogo = () => (
  <img
    className="jt-conn-error-logo"
    src={CONNECTION_ERROR_LOGO_URL}
    alt=""
    onError={(event) => { event.currentTarget.style.display = 'none'; }}
  />
);

/**
 * Shown in place of a loading spinner once that spinner has run past the
 * configured timeout (see hooks/useLoadingTimeout.js) — i.e. the data never
 * arrived and the guest is owed an explanation and a way out.
 *
 * Reusable on purpose: every loading state in the app renders the same
 * screen, so a stalled config load, a stalled booking lookup and a stalled
 * admin query all look and behave identically.
 *
 * @param {object} props
 * @param {string} [props.title] - headline; defaults to the shared copy.
 * @param {string} [props.message] - override the shared copy. Every screen
 *   currently uses the default (CONNECTION_ERROR_MESSAGE) so a guest sees the
 *   same wording wherever a load stalls; pass this only if one screen ever
 *   genuinely needs to say something different.
 * @param {() => void} [props.onRetry] - re-runs the caller's own fetch. The
 *   button is hidden entirely when this is omitted, since a button that does
 *   nothing is worse than none.
 * @param {string} [props.retryLabel] - button text.
 * @param {'screen'|'inline'} [props.variant] - 'screen' fills the viewport
 *   (app boot); 'inline' fills its container instead, for a modal or a panel
 *   that still has its own chrome and close button around it.
 * @returns {JSX.Element}
 */
function ConnectionErrorScreen({
  title = CONNECTION_ERROR_TITLE,
  message = CONNECTION_ERROR_MESSAGE,
  onRetry,
  retryLabel = CONNECTION_ERROR_RETRY_LABEL,
  variant = 'screen',
}) {
  return (
    <div
      className={`jt-conn-error jt-conn-error--${variant}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="jt-conn-error-inner">
        <h2 className="jt-conn-error-title">{title}</h2>
        <ClientLogo />
        <p className="jt-conn-error-message">{message}</p>
        {onRetry && (
          <button type="button" className="jt-conn-error-retry" onClick={onRetry}>
            {retryLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export default ConnectionErrorScreen;
